/**
 * In production, shows a confirmation modal before mutations for developers
 * (users with the `Developer` function), to avoid accidental changes.
 */

import { Alert } from '@/services/alert.service';
import { i18n } from '@/i18n';

const MUTATION_METHODS = ['post', 'put', 'patch', 'delete'] as const;
const FIVE_MIN_MS = 5 * 60 * 1000;
const ALLOW_5MIN_STORAGE_KEY = 'prod-mutation-guard-allow-until';

let isCurrentUserDeveloper = false;

/**
 * Call this when the current user is known (e.g. after login / token payload load).
 */
export function setProdMutationGuardCurrentUserIsDeveloper(isDeveloper: boolean): void {
  isCurrentUserDeveloper = isDeveloper;
}

export function isProdMutationGuardActive(): boolean {
  if (import.meta.env.VITE_ENVIRONMENT !== 'production') return false;
  return isCurrentUserDeveloper;
}

function isAllowedForFiveMinutes(): boolean {
  try {
    const until = sessionStorage.getItem(ALLOW_5MIN_STORAGE_KEY);
    if (!until) return false;
    const ts = parseInt(until, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() < ts;
  } catch {
    return false;
  }
}

function setAllowedForFiveMinutes(): void {
  try {
    sessionStorage.setItem(ALLOW_5MIN_STORAGE_KEY, String(Date.now() + FIVE_MIN_MS));
  } catch {
    // ignore
  }
}

export type ProdMutationGuardResult = 'allow' | 'cancel';

/**
 * Shows the confirmation modal. Resolves with 'allow' if the user chose allow once or allow 5 min,
 * and 'cancel' if they chose cancel. Caller should abort the request when result is 'cancel'.
 */
export async function confirmMutationBeforeSend(): Promise<ProdMutationGuardResult> {
  if (!isProdMutationGuardActive()) return 'allow';
  if (isAllowedForFiveMinutes()) return 'allow';

  const t = i18n.global.t;
  const result = await Alert.fire<undefined, 'allowOnce' | 'allow5Min' | 'cancel'>({
    icon: 'warning',
    title: t('prodMutationGuard.title'),
    text: t('prodMutationGuard.description'),
    buttons: [
      { propertyName: 'allowOnce', text: t('prodMutationGuard.allowOnce'), style: 'default' },
      { propertyName: 'allow5Min', text: t('prodMutationGuard.allow5Min'), style: 'secondary' },
      { propertyName: 'cancel', text: t('prodMutationGuard.cancel'), style: 'ghost' },
    ],
  });

  if (result.cancel) return 'cancel';
  if (result.allow5Min) {
    setAllowedForFiveMinutes();
  }
  return 'allow';
}

export function isMutationMethod(method: string | undefined): boolean {
  return (
    !!method && MUTATION_METHODS.includes(method.toLowerCase() as (typeof MUTATION_METHODS)[number])
  );
}
